// A Default Filters user field (Component 73): a bounded, server-searched user lookup for a
// read-only list filter. Production's Tool Requests "Requested By" (gitlab/develop
// src/components/datagrid/UserFilterAutocomplete.tsx), lifted for the shared panel's
// `defaultFilters[].renderField`. Local changes: the label is the caller's (upstream hard-codes
// "Requested By"); the search goes as `q`, as this repo's UserAutocomplete sends it; the stock
// popup icon (upstream's FilterSelectIcon is not in this repo).
import { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import queryString from "query-string";
import { useSnackbar } from "notistack";

import { axios } from "../../api";

interface UserOption {
  id: number;
  name: string;
}

export default function UserFilterAutocomplete({
  value,
  onChange,
  label,
}: {
  /** The picked user's id as a string; "" = no filter. */
  value: string;
  onChange: (value: string) => void;
  /** Placeholder and accessible name ("Person responsible"). */
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<UserOption[]>([]);
  const [selected, setSelected] = useState<UserOption | null>(null);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params: Record<string, unknown> = {
          filter: [JSON.stringify({ field: "active", op: "==", value: 1 })],
          page: 1,
          size: 50,
          sort: "name",
        };
        if (search.trim()) params.q = search.trim();
        const { data } = await axios.get<{ items: UserOption[] }>("/v6/users", {
          params,
          paramsSerializer: (p) => queryString.stringify(p),
          signal: controller.signal,
        });
        if (!controller.signal.aborted) setOptions(data.items ?? []);
      } catch {
        if (!controller.signal.aborted) enqueueSnackbar("Error fetching users", { variant: "error" });
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, search, enqueueSnackbar]);

  const current = value
    ? options.find((o) => String(o.id) === value) ??
      (selected && String(selected.id) === value ? selected : { id: Number(value), name: `#${value}` })
    : null;
  const allOptions = current && !options.some((o) => o.id === current.id) ? [current, ...options] : options;

  return (
    <Autocomplete<UserOption>
      fullWidth
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={allOptions}
      value={current}
      loading={loading}
      filterOptions={(items) => items}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onInputChange={(_, text, reason) => {
        if (reason === "input") setSearch(text);
      }}
      onChange={(_, item) => {
        setSelected(item);
        onChange(item ? String(item.id) : "");
        setSearch("");
      }}
      componentsProps={{ popper: { sx: { "& .MuiAutocomplete-listbox": { maxHeight: 280 } } } }}
      renderInput={(params) => (
        <TextField {...params} placeholder={label} inputProps={{ ...params.inputProps, "aria-label": label }} />
      )}
    />
  );
}
